import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  CollabQuickActionContext,
  CollabQuickActionName,
  DatesHaveBiggerGapPipe,
  DownloadAttachmentsService,
  MessageComponent,
  MessagesInSameThreadPipe,
  MessageVM,
  NewMessageMarkerComponent,
} from '@libs/collaboration';
import { InfiniteScrollListDirective, InfiniteScrollListItemDirective } from '@libs/ui-lib';
import { AssistantSystemMessageComponent } from '../assistant-system-message';
import { AssistantWelcomeBannerComponent } from '../assistant-welcome-banner';

@Component({
  selector: 'lib-assistant-message-list',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MessageComponent,
    NewMessageMarkerComponent,
    AssistantSystemMessageComponent,
    AssistantWelcomeBannerComponent,
    DatesHaveBiggerGapPipe,
    MessagesInSameThreadPipe,
    InfiniteScrollListDirective,
    InfiniteScrollListItemDirective,
  ],
  templateUrl: './assistant-message-list.component.html',
  styleUrl: './assistant-message-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantMessageListComponent {
  public readonly messages = input.required<MessageVM[]>();
  public readonly showWelcome = input(false);
  // A null text means the default translation
  public readonly welcomeTitle = input<string | null>(null);
  public readonly welcomeDescription = input<string | null>(null);
  public readonly participantCount = input(0);
  public readonly newMessageMarkerBeforeMessageId = input<string>();
  public readonly editingMessageId = input<string | null>(null);
  public readonly historyOpenMessageId = input<string | null>(null);
  public readonly quickActionContext = input<CollabQuickActionContext | null>(null);

  public readonly loadOlderMessages = output<void>();
  public readonly unsendMessage = output<string>();
  public readonly markMessageUnread = output<string>();
  public readonly retrySendMessage = output<string>();
  public readonly reply = output<MessageVM>();
  public readonly threadClicked = output<MessageVM>();
  public readonly startEdit = output<string>();
  public readonly cancelEdit = output<void>();
  public readonly toggleHistory = output<string>();
  public readonly editMessage = output<{ messageId: string; text: string }>();
  public readonly namedQuickAction = output<CollabQuickActionName>();

  @ViewChild('scrollContainer') private readonly scrollContainer!: ElementRef<HTMLDivElement>;

  private readonly downloadAttachmentsService = inject(DownloadAttachmentsService);

  private readonly messageInDownloadMode = computed(() => this.downloadAttachmentsService.viewModel().messageInDownloadMode);

  /**
   * lib-message only sets up the download state and emits - something has to drive the root
   * DownloadAttachmentsService, which collab-screen does via its own handlers. Without this the Download menu
   * item did nothing in the assistant. Unlike collab-screen, cancelling a selection here just resets, since the
   * chat window has no confirm dialog.
   */
  protected onDownloadTap(messageId: string): void {
    if (this.messageInDownloadMode() === messageId) {
      this.downloadAttachmentsService.resetMessageInDownloadMode();

      return;
    }

    this.downloadAttachmentsService.setMessageInDownloadMode(messageId);
  }

  protected onDownloadAttachments(): void {
    this.downloadAttachmentsService.downloadAttachments();
    this.downloadAttachmentsService.resetMessageInDownloadMode();
  }

  protected onMouseWheel(event: WheelEvent): void {
    event.preventDefault();
    this.scrollContainer.nativeElement.scrollTop -= event.deltaY;
  }

  // The intro bubble trails the user's own latest message until Customer Success answers, so it stays at
  // the bottom of the thread as the user sends more. Once any reply arrives it is gone for good.
  private readonly systemMessageAfterMessageId = computed(() => {
    const messages = this.messages().filter((message) => !message.isSystemMessage);
    const hasReply = messages.some((message) => !message.sender.isSelf && !message.softDeletedAt);

    return hasReply ? undefined : messages.filter((message) => message.sender.isSelf).at(-1)?.id;
  });

  protected showSystemMessageAfter(message: MessageVM): boolean {
    return message.id === this.systemMessageAfterMessageId();
  }
}
